"use client"

import {
  Children,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type PointerEvent,
  type MouseEvent,
} from "react"

import { cn } from "@local/utils/src/cn"
import { getT } from "@local/i18n/src/t"
import { getLanguageForMarket } from "@local/i18n/src/languages"
import { a11y } from "@local/i18n/src/catalogs/a11y"
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from "../icons"

export interface CssCarouselProps {
  children: ReactNode
  className?: string
  initialSlide?: number
  afterSlide?: (index: number) => void
  showArrows?: boolean
  roundedArrows?: boolean
  showScroll?: boolean
  showDots?: boolean
  /**
   * Colour scheme for the pagination dots.
   * "dark" (default) suits light backgrounds; "light" suits dark backgrounds.
   */
  dotsVariant?: "light" | "dark"
  /** Show a "1/10" style counter instead of individual dots */
  showCounter?: boolean
  slidesPerView?: {
    base?: number
    sm?: number
    md?: number
  }
  showThumbs?: boolean
  infiniteScroll?: boolean
  onClick?: () => void
  ariaLabel?: string
  /**
   * Market slug (e.g. "de"). When provided, the arrow aria-labels are
   * localized for that market's language; absent, they default to English.
   */
  market?: string
}

export const CssCarousel = ({
  children,
  className,
  initialSlide = 0,
  afterSlide,
  showArrows = false,
  roundedArrows = false,
  showScroll = false,
  showDots = false,
  dotsVariant = "dark",
  showCounter = false,
  slidesPerView,
  showThumbs = false,
  infiniteScroll = true,
  onClick,
  ariaLabel,
  market,
}: CssCarouselProps) => {
  const t = getT(getLanguageForMarket(market), { a11y })
  const scrollRef = useRef<HTMLUListElement>(null)
  const [thumbWidthPct, setThumbWidthPct] = useState<number>(0)
  const [thumbLeftPct, setThumbLeftPct] = useState<number>(0)
  const [currentIndex, setCurrentIndex] = useState<number>(initialSlide)
  const [computedMinWidth, setComputedMinWidth] = useState<string>(() => {
    // Default to desktop density (md) before client breakpoints are applied
    const gapPx = 24
    const mdCount = slidesPerView?.md ?? 4
    const gaps = Math.max(0, Math.floor(mdCount) - 1)
    const totalGapPx = gaps * gapPx
    return `calc((100% - ${totalGapPx}px)/${mdCount})`
  })

  const slides = useMemo(() => Children.toArray(children), [children])

  // Compute min-width via matchMedia to avoid stale initial window width on refresh
  useLayoutEffect(() => {
    const gapPx = 24
    const baseCount = slidesPerView?.base ?? 1.5
    const smCount = slidesPerView?.sm ?? 2
    const mdCount = slidesPerView?.md ?? 4

    const compute = () => {
      if (typeof window === "undefined") return
      if (window.matchMedia("(min-width: 768px)").matches) {
        const gaps = Math.max(0, Math.floor(mdCount) - 1)
        const totalGapPx = gaps * gapPx
        setComputedMinWidth(`calc((100% - ${totalGapPx}px)/${mdCount})`)
        return
      }
      if (window.matchMedia("(min-width: 640px)").matches) {
        const gaps = Math.max(0, Math.floor(smCount) - 1)
        const totalGapPx = gaps * gapPx
        setComputedMinWidth(`calc((100% - ${totalGapPx}px)/${smCount})`)
        return
      }
      // Mobile (<640px): use base (e.g., 1.5) once breakpoints are evaluated
      setComputedMinWidth(`${100 / baseCount}%`)
    }

    compute()
    window?.addEventListener?.("resize", compute)
    return () => window?.removeEventListener?.("resize", compute)
  }, [slidesPerView?.base, slidesPerView?.sm, slidesPerView?.md])

  const updateFromScroll = () => {
    const list = scrollRef.current
    if (!list) return

    const firstSlide = list.querySelector("li") as HTMLElement | null
    const gapPx = 24 // gap-6
    const step = firstSlide ? firstSlide.offsetWidth + gapPx : 0

    let snappedIndex = 0
    if (step > 0) {
      snappedIndex = Math.round(list.scrollLeft / step)
    }

    // Calculate visible slides based on current breakpoint
    let visibleSlides = slidesPerView?.md ?? 4
    if (typeof window !== "undefined") {
      if (window.matchMedia("(max-width: 767px)").matches) {
        if (window.matchMedia("(max-width: 639px)").matches) {
          visibleSlides = slidesPerView?.base ?? 1.5
        } else {
          visibleSlides = slidesPerView?.sm ?? 2
        }
      }
    }

    // Calculate thumb width based on visible slides vs total slides
    const totalSlides = slides.length
    const thumbWidth =
      totalSlides > 0 ? (visibleSlides / totalSlides) * 100 : 100
    setThumbWidthPct(Math.max(0, Math.min(100, thumbWidth)))

    // Calculate thumb position based on current index and scrollable positions
    const maxScrollableIndex = Math.max(0, totalSlides - visibleSlides)
    const normalizedIndex = Math.max(
      0,
      Math.min(maxScrollableIndex, snappedIndex)
    )
    const thumbLeft =
      maxScrollableIndex > 0
        ? (normalizedIndex / maxScrollableIndex) * (100 - thumbWidth)
        : 0
    setThumbLeftPct(Math.max(0, Math.min(100 - thumbWidth, thumbLeft)))

    // Fire afterSlide when the snapped index changes
    setCurrentIndex((prev) => {
      if (prev !== snappedIndex) {
        afterSlide?.(snappedIndex)
      }
      return snappedIndex
    })
  }

  // Initial positioning to initialSlide once dimensions are known
  useEffect(() => {
    const list = scrollRef.current
    if (!list) return
    const firstSlide = list.querySelector("li") as HTMLElement | null
    const gapPx = 24
    const step = firstSlide ? firstSlide.offsetWidth + gapPx : 0
    const left = step > 0 ? initialSlide * step : 0
    list.scrollTo({ left, behavior: "auto" })
    // Update thumb once positioned
    updateFromScroll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSlide, computedMinWidth, slides.length])

  // Attach scroll + resize handlers
  useEffect(() => {
    const list = scrollRef.current
    if (!list) return
    const onScroll = () => updateFromScroll()
    list?.addEventListener?.("scroll", onScroll, { passive: true })
    window?.addEventListener?.("resize", updateFromScroll)
    // Do an initial update
    updateFromScroll()
    return () => {
      list?.removeEventListener?.("scroll", onScroll)
      window?.removeEventListener?.("resize", updateFromScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedMinWidth, slides.length])

  const scrollByStep = (direction: 1 | -1) => {
    const list = scrollRef.current
    if (!list) return
    const firstSlide = list.querySelector("li") as HTMLElement | null
    const gapPx = 24
    const step = firstSlide
      ? firstSlide.offsetWidth + gapPx
      : Math.floor(list.clientWidth * 0.8)
    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth)
    const nextLeft = Math.max(
      0,
      Math.min(maxScroll, list.scrollLeft + direction * step)
    )
    list.scrollTo({ left: nextLeft, behavior: "smooth" })
  }

  const scrollToIndex = (index: number) => {
    const list = scrollRef.current
    if (!list) return
    const firstSlide = list.querySelector("li") as HTMLElement | null
    const gapPx = 24
    const step = firstSlide
      ? firstSlide.offsetWidth + gapPx
      : Math.floor(list.clientWidth * 0.8)
    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth)
    const visibleCount =
      step > 0 ? Math.max(1, Math.round(list.clientWidth / step)) : 1
    const maxIndex = Math.max(0, slides.length - visibleCount)
    const clampedIndex = Math.max(0, Math.min(maxIndex, index))
    const left = clampedIndex >= maxIndex ? maxScroll : clampedIndex * step
    list.scrollTo({ left: Math.min(maxScroll, left), behavior: "smooth" })
  }

  // Lightweight drag-to-scroll with short gesture step and click preservation
  const isPointerDownRef = useRef<boolean>(false)
  const dragMovedRef = useRef<boolean>(false)
  const lockedAxisRef = useRef<"x" | "y" | null>(null)
  const startXRef = useRef<number>(0)
  const startYRef = useRef<number>(0)
  const startScrollLeftRef = useRef<number>(0)
  const cancelClickRef = useRef<boolean>(false)

  const onPointerDown = (e: PointerEvent<HTMLUListElement>) => {
    const list = scrollRef.current
    if (!list) return
    isPointerDownRef.current = true
    dragMovedRef.current = false
    lockedAxisRef.current = null
    cancelClickRef.current = false
    startXRef.current = e.clientX
    startYRef.current = e.clientY
    startScrollLeftRef.current = list.scrollLeft
  }

  const onPointerMove = (e: PointerEvent<HTMLUListElement>) => {
    const list = scrollRef.current
    if (!list || !isPointerDownRef.current) return

    // If already locked to vertical scrolling, don't interfere
    if (lockedAxisRef.current === "y") return

    const dx = e.clientX - startXRef.current
    const dy = e.clientY - startYRef.current

    // Determine axis intent with a small threshold
    if (!lockedAxisRef.current) {
      const absX = Math.abs(dx)
      const absY = Math.abs(dy)

      // Prioritize vertical scrolling - if vertical movement is detected first, release control
      if (absY > 8 && absY > absX * 1.5) {
        lockedAxisRef.current = "y"
        // Release pointer control completely to allow native vertical scrolling
        isPointerDownRef.current = false
        return
      } else if (absX > 6 && absX > absY) {
        lockedAxisRef.current = "x"
        cancelClickRef.current = true
        try {
          ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
        } catch {
          // Fails in silence
        }
      } else {
        return
      }
    }

    if (lockedAxisRef.current === "x") {
      dragMovedRef.current = true
      e.preventDefault()
      list.scrollLeft = startScrollLeftRef.current - dx
    }
  }

  const stepToIndex = (startLeft: number, currentLeft: number) => {
    const list = scrollRef.current
    if (!list) return 0
    const firstSlide = list.querySelector("li") as HTMLElement | null
    const gapPx = 24
    const step = firstSlide ? firstSlide.offsetWidth + gapPx : 0
    if (step <= 0) return Math.round(currentLeft / (list.clientWidth || 1))
    const baseIndex = Math.round(startLeft / step)
    const delta = currentLeft - startLeft
    const minAdvancePx = Math.min(20, step * 0.1)
    if (delta > minAdvancePx) return baseIndex + 1
    if (delta < -minAdvancePx) return baseIndex - 1
    return baseIndex
  }

  const finishDrag = () => {
    const list = scrollRef.current
    if (!list) return
    const firstSlide = list.querySelector("li") as HTMLElement | null
    const gapPx = 24
    const step = firstSlide ? firstSlide.offsetWidth + gapPx : 0
    let targetIndex = stepToIndex(startScrollLeftRef.current, list.scrollLeft)
    if (step > 0) {
      const visibleCount = Math.max(1, Math.round(list.clientWidth / step))
      const maxIndex = Math.max(0, slides.length - visibleCount)
      targetIndex = Math.max(0, Math.min(maxIndex, targetIndex))
      const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth)
      const targetLeft =
        targetIndex >= maxIndex ? maxScroll : targetIndex * step
      list.scrollTo({ left: targetLeft, behavior: "smooth" })
    }
  }

  const onPointerUpOrCancel = () => {
    if (!isPointerDownRef.current) return
    if (dragMovedRef.current && lockedAxisRef.current === "x") finishDrag()
    isPointerDownRef.current = false
    dragMovedRef.current = false
    lockedAxisRef.current = null
    // Allow clicks again for the next interaction
  }

  const onClickCapture = (e: MouseEvent<HTMLUListElement>) => {
    // If a horizontal drag occurred, swallow the click; otherwise allow it
    if (cancelClickRef.current && dragMovedRef.current) {
      e.preventDefault()
      e.stopPropagation()
    }
    cancelClickRef.current = false
  }

  const id = useId()

  const onScrollKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    if (e.key === "ArrowRight") {
      e.preventDefault()
      scrollByStep(1)
    } else if (e.key === "ArrowLeft") {
      e.preventDefault()
      scrollByStep(-1)
    }
  }

  return (
    <div
      role="region"
      aria-label={ariaLabel || t("a11y.carousel", { id })}
      aria-roledescription="carousel"
      className={cn("relative", className)}
    >
      <div className="relative">
        {showArrows && roundedArrows ? (
          <>
            {(currentIndex > 0 || infiniteScroll) && (
              <button
                type="button"
                aria-label={t("a11y.scrollLeft")}
                className="absolute top-2/5 left-2 z-10 flex h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-105 active:scale-95 sm:-left-10 sm:h-16 sm:w-16"
                onClick={() => scrollByStep(-1)}
              >
                <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            )}
            {(currentIndex < slides.length - 1 || infiniteScroll) && (
              <button
                type="button"
                aria-label={t("a11y.scrollRight")}
                className="absolute top-2/5 right-2 z-10 flex h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-105 active:scale-95 sm:-right-10 sm:h-16 sm:w-16"
                onClick={() => scrollByStep(1)}
              >
                <ArrowLeftIcon className="h-4 w-4 rotate-180 sm:h-5 sm:w-5" />
              </button>
            )}
          </>
        ) : showArrows ? (
          <>
            {(currentIndex > 0 || infiniteScroll) && (
              <button
                type="button"
                aria-label={t("a11y.scrollLeft")}
                className="font-display absolute top-1/2 left-0 z-10 flex h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center text-5xl uppercase transition-transform hover:scale-105 active:scale-95 sm:h-16 sm:w-16 sm:text-6xl"
                onClick={() => scrollByStep(-1)}
              >
                <ChevronLeftIcon className="text-gray-400" />
              </button>
            )}
            {(currentIndex < slides.length - 1 || infiniteScroll) && (
              <button
                type="button"
                aria-label={t("a11y.scrollRight")}
                className="font-display absolute top-1/2 right-0 z-10 flex h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center text-5xl uppercase transition-transform hover:scale-105 active:scale-95 sm:h-16 sm:w-16 sm:text-6xl"
                onClick={() => scrollByStep(1)}
              >
                <ChevronRightIcon className="text-gray-400" />
              </button>
            )}
          </>
        ) : (
          <></>
        )}

        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex -- scroll container is focusable so keyboard users can pan the carousel with ArrowLeft/ArrowRight */}
        <ul
          ref={scrollRef}
          tabIndex={0}
          className="flex touch-pan-x [touch-action:pan-x_pan-y] snap-x snap-mandatory gap-6 overflow-x-auto overflow-y-hidden scroll-smooth [-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 md:px-0 [&::-webkit-scrollbar]:hidden"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUpOrCancel}
          onPointerCancel={onPointerUpOrCancel}
          onClickCapture={onClickCapture}
          onKeyDown={onScrollKeyDown}
        >
          {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          {slides.map((child, i) => (
            <li
              key={`css-carousel-slide-${i}-${id}`}
              className="max-w-full flex-none snap-start list-none overflow-hidden"
              style={{ width: computedMinWidth }}
            >
              {onClick ? (
                <button
                  type="button"
                  className="block h-full w-full cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
                  aria-label={t("a11y.slideNofM", {
                    n: i + 1,
                    total: slides.length,
                  })}
                  onClick={onClick}
                >
                  {child}
                </button>
              ) : (
                child
              )}
            </li>
          ))}
        </ul>
      </div>

      {showScroll && (
        <div className="flex justify-center">
          <div className="relative mt-4 w-4/5 rounded-full" aria-hidden>
            <div className="h-2 rounded-full bg-gray-100" />
            <div
              className="absolute top-0 h-2 rounded-full bg-black transition-[left,width] duration-50 ease-out"
              style={{ left: `${thumbLeftPct}%`, width: `${thumbWidthPct}%` }}
            />
          </div>
        </div>
      )}

      {showThumbs && (
        <div className="flex w-full flex-row justify-center">
          <div className="relative w-full overflow-hidden">
            <ul
              className="flex transition-transform duration-300 ease-out"
              style={{
                transform: `translateX(-${Math.max(0, Math.min(currentIndex - 2, slides.length - 5)) * (100 / slides.length)}%)`,
                width: `${slides.length * 20}%`,
              }}
            >
              {slides.map((child, i) => (
                <li
                  className="flex-shrink-0 list-none"
                  key={`css-carousel-thumb-${i}`}
                  style={{ width: `${100 / slides.length}%` }}
                >
                  <button
                    type="button"
                    className="block w-full cursor-pointer focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
                    aria-label={t("a11y.goToSlide", { n: i + 1 })}
                    aria-current={i === currentIndex ? "true" : undefined}
                    onClick={() => scrollToIndex(i)}
                  >
                    <div
                      className={cn(
                        "transition-opacity duration-200",
                        i === currentIndex
                          ? "opacity-100"
                          : "opacity-60 hover:opacity-80"
                      )}
                    >
                      {child}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {showDots && !showCounter && slides.length > 1 && (
        <div className="mt-3 flex max-w-full flex-wrap items-center justify-center gap-2">
          {slides.map((_, i) => {
            const isActive = i === currentIndex
            return (
              <button
                key={`dot-${i}`}
                type="button"
                aria-label={t("a11y.goToSlide", { n: i + 1 })}
                aria-current={isActive ? "true" : undefined}
                className={cn(
                  "h-2.5 w-2.5 flex-shrink-0 rounded-full transition-colors",
                  dotsVariant === "light"
                    ? isActive
                      ? "bg-white"
                      : "bg-gray-400 hover:bg-gray-300"
                    : isActive
                      ? "bg-black"
                      : "bg-gray-500 hover:bg-gray-600"
                )}
                onClick={() => scrollToIndex(i)}
              />
            )
          })}
        </div>
      )}

      {showCounter && slides.length > 1 && (
        <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/90 px-3 py-1 text-xs font-medium text-white">
          {currentIndex + 1}/{slides.length}
        </span>
      )}
    </div>
  )
}

export default CssCarousel
